import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { FaArrowLeft, FaArrowRight, FaBook, FaSave, FaEye, FaEdit, FaTrash, FaExpand, FaTimes } from 'react-icons/fa';
import CustomModal from '../components/CustomModal';
import { backendConfig } from '../config/backendConfig';
import { storage, db } from '../firebase';
import { ref as storageRef, getDownloadURL } from 'firebase/storage';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { auth } from '../firebase';

const backendBase = backendConfig.baseUrl;

// Add CSS styles for page flip animation
const pageFlipStyles = `
  @keyframes pageFlip {
    0% { transform: rotateY(0deg); }
    50% { transform: rotateY(90deg); }
    100% { transform: rotateY(0deg); }
  }
  
  .page-flip {
    animation: pageFlip 0.6s ease-in-out;
    transform-style: preserve-3d;
  }
  
  .book-container {
    perspective: 1000px;
  }
`;

export default function BookView() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(!!id);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [isBookOpen, setIsBookOpen] = useState(false);
  const [isFlipping, setIsFlipping] = useState(false);
  const descRef = useRef(null);
  const [highlightEdit, setHighlightEdit] = useState(false);
  const [draftDescriptions, setDraftDescriptions] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState(-1);
  const [isEditing, setIsEditing] = useState(false);
  const [showArtworkModal, setShowArtworkModal] = useState(false);
  const [isEditingSynopsis, setIsEditingSynopsis] = useState(false);
  const [editedSynopsis, setEditedSynopsis] = useState('');
  const [isSavingSynopsis, setIsSavingSynopsis] = useState(false);
  const [isEditingAbout, setIsEditingAbout] = useState(false);
  const [editedAbout, setEditedAbout] = useState('');
  const [isSavingAbout, setIsSavingAbout] = useState(false);
  const [authorName, setAuthorName] = useState(null);
  const [showSaveSuccessModal, setShowSaveSuccessModal] = useState(false);
  const [saveSuccessMessage, setSaveSuccessMessage] = useState('');
  const [showSaveErrorModal, setShowSaveErrorModal] = useState(false);
  const [saveErrorMessage, setSaveErrorMessage] = useState('');
  
  // Get return path from location state
  const returnPath = location.state?.returnPath;
  const returnState = location.state?.returnState;

  // Resolve image URLs from various sources (BFL proxy, relative, gs://, localhost)
  const resolveImageUrl = async (url) => {
    if (!url || typeof url !== 'string') return url;
    const trimmed = url.trim();
    if (!trimmed) return url;
    
    // localhost:3001 → convert to backend base URL or Firebase Storage
    if (trimmed.includes('localhost:3001')) {
      // Extract the path from localhost URL
      const urlPath = trimmed.replace(/^https?:\/\/[^/]+/, '');
      // Check if it's a Firebase Storage path (uploads/enhanced_scene_...)
      if (urlPath.includes('/uploads/') || urlPath.includes('enhanced_scene_')) {
        // Try to resolve from Firebase Storage first
        try {
          // Extract filename from path like /uploads/enhanced_scene_1234567890_abc.jpg
          const filename = urlPath.split('/').pop();
          // Try common Firebase Storage paths
          const possiblePaths = [
            `story-images/${filename}`,
            `art-stories/${filename}`,
            `uploads/${filename}`,
            `enhanced-scenes/${filename}`
          ];
          
          for (const path of possiblePaths) {
            try {
              const r = storageRef(storage, path);
              const dl = await getDownloadURL(r);
              console.log(`✅ Resolved localhost URL to Firebase Storage: ${trimmed} → ${dl}`);
              return dl;
            } catch (e) {
              // Continue to next path
            }
          }
        } catch (e) {
          console.warn('Failed to resolve localhost URL from Firebase Storage', trimmed, e.message);
        }
      }
      // Fallback: use backend proxy-image endpoint which handles /uploads/ paths
      const base = backendConfig.baseUrl;
      const resolved = `${base}/proxy-image?url=${encodeURIComponent(urlPath)}`;
      console.log(`🔄 Resolved localhost URL to backend proxy: ${trimmed} → ${resolved}`);
      return resolved;
    }
    
    // gs:// → HTTPS download URL
    if (trimmed.startsWith('gs://')) {
      try {
        const withoutScheme = trimmed.replace(/^gs:\/\//, '');
        const firstSlash = withoutScheme.indexOf('/');
        const objectPath = firstSlash >= 0 ? withoutScheme.slice(firstSlash + 1) : withoutScheme;
        const r = storageRef(storage, objectPath);
        const dl = await getDownloadURL(r);
        return dl;
      } catch (e) {
        console.warn('Failed to resolve gs:// URL in BookView', trimmed, e.message);
      }
    }
    // BFL CDN → proxy through backend
    if (trimmed.includes('bfl.ai') || trimmed.includes('delivery-eu4.bfl.ai')) {
      const base = backendConfig.baseUrl;
      return `${base}/proxy-image?url=${encodeURIComponent(trimmed)}`;
    }
    // Relative path → prefix backend base
    if (!/^https?:\/\//i.test(trimmed) && !trimmed.startsWith('data:')) {
      const base = backendConfig.baseUrl;
      return `${base}${trimmed.startsWith('/') ? '' : '/'}${trimmed}`;
    }
    return trimmed;
  };

  // Normalize story image fields (cover and scenes) to resolvable URLs
  const normalizeStoryImages = async (s) => {
    if (!s) return s;
    const next = { ...s };
    const coverCandidate = next.coverUrl || next.originalArtwork;
    next.coverUrl = await resolveImageUrl(coverCandidate);
    if (Array.isArray(next.scenes)) {
      next.scenes = await Promise.all(next.scenes.map(async (scene) => {
        const ns = { ...scene };
        const keys = ['imageUrl', 'refinedImageUrl', 'url', 'image', 'coverUrl'];
        for (const k of keys) {
          if (ns[k]) ns[k] = await resolveImageUrl(ns[k]);
        }
        return ns;
      }));
    }
    return next;
  };

  // Prepare scenes array for synopsis generation (must be declared before any early returns)
  const scenesForSynopsis = useMemo(() => (story?.scenes || []), [story]);

  // Generate a spoiler-free synopsis from scene content when brief is missing
  const autoBrief = useMemo(() => {
    if (!scenesForSynopsis || scenesForSynopsis.length === 0) return '';

    const clamp = (str, max) => (str && str.length > max ? str.slice(0, max).trim() + '…' : str || '');

    const stripSpoilers = (text) => {
      if (!text) return '';
      const spoilerWords = /(finally|at last|in the end|ultimately|concludes|resolution|the end|conclusion|reveals the secret)/i;
      return text
        .split(/(?<=[.!?])\s+/)
        .filter(s => !spoilerWords.test(s))
        .join(' ')
        .trim();
    };

    const first = scenesForSynopsis[0] || {};
    const second = scenesForSynopsis[1] || {};

    const allCharacters = Array.from(new Set((scenesForSynopsis || []).flatMap(s => (s.characters || []).map(c => c.name).filter(Boolean))));
    const protagonist = allCharacters.slice(0, 2).join(' and ');

    const settings = Array.from(new Set((scenesForSynopsis || []).map(s => s.setting).filter(Boolean)));
    const primarySetting = settings[0] || '';

    const titles = Array.from(new Set((scenesForSynopsis || []).map(s => s.title).filter(Boolean)));

    const firstDesc = stripSpoilers(first.description || '');
    const secondDesc = stripSpoilers(second.description || '');

    const parts = [];
    if (protagonist && primarySetting) {
      parts.push(`A story following ${protagonist} in ${primarySetting}, where everyday moments unfold with warmth and curiosity.`);
    } else if (primarySetting) {
      parts.push(`Set in ${primarySetting}, this tale explores a gentle journey filled with discovery.`);
    } else if (protagonist) {
      parts.push(`A gentle journey with ${protagonist}, shaped by small choices and meaningful moments.`);
    } else {
      parts.push('A gentle journey of discovery told through scene-by-scene moments.');
    }

    const themeHint = titles.slice(0, 3).join(', ');
    if (themeHint) {
      parts.push(`Themes of ${themeHint.toLowerCase()} guide the path forward without giving everything away.`);
    }

    const texture = clamp(`${firstDesc} ${secondDesc}`.trim(), 240);
    if (texture) {
      parts.push(texture);
    }

    const synopsis = parts.join(' ').replace(/\s+/g, ' ').trim();
    return synopsis || '';
  }, [scenesForSynopsis]);

  useEffect(() => {
    if (!id) {
      // Preview mode: use location.state
      if (location.state) {
        (async () => {
          const normalized = await normalizeStoryImages(location.state);
          setStory(normalized);
        })();
        setLoading(false);
      } else {
        setError('No story data found for preview.');
        setLoading(false);
      }
      return;
    }
    // Saved mode: fetch from backend
    async function fetchStory() {
      setLoading(true);
      try {
        const res = await fetch(`${backendBase}/api/stories/${id}`);
        if (!res.ok) throw new Error('Failed to fetch story');
        const data = await res.json();
        const normalized = await normalizeStoryImages(data);
        setStory(normalized);
        // Optional: jump to specific scene if specified in query
        try {
          const params = new URLSearchParams(location.search);
          const sceneParam = parseInt(params.get('scene') || '', 10);
          if (!Number.isNaN(sceneParam) && sceneParam > 0) {
            // scene pages start at index 4
            const clamped = Math.min(data.scenes?.length || 0, Math.max(1, sceneParam));
            setPage(3 + clamped); // 3 + N => page index for scene N
          }
        } catch (_) {}
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    fetchStory();
  }, [id, location.state, location.search]);

  // Fetch author name from user profile
  useEffect(() => {
    const fetchAuthorName = async () => {
      if (!story?.userId) {
        setAuthorName(null);
        return;
      }
      
      try {
        const userDocRef = doc(db, 'users', story.userId);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          const userData = userDoc.data();
          // Try displayName first, then name, then email, then fallback
          const name = userData.displayName || userData.name || userData.email?.split('@')[0] || null;
          setAuthorName(name);
        } else {
          setAuthorName(null);
        }
      } catch (error) {
        console.error('Error fetching author name:', error);
        setAuthorName(null);
      }
    };

    if (story) {
      fetchAuthorName();
    }
  }, [story]);

  // Animate book opening
  useEffect(() => {
    if (story) {
      setTimeout(() => setIsBookOpen(true), 500);
      // reset edit state when story changes
      setIsEditing(false);
      // Initialize edited synopsis and about when story loads
      if (story.brief) {
        setEditedSynopsis(story.brief);
      }
      if (story.about) {
        setEditedAbout(story.about);
      }
    }
  }, [story]);

  // Handle synopsis editing
  const handleEditSynopsis = () => {
    const currentBrief = story?.brief || autoBrief || '';
    setEditedSynopsis(currentBrief);
    setIsEditingSynopsis(true);
  };

  const handleSaveSynopsis = async () => {
    const storyIdToUse = id || story?.storyId;
    if (!storyIdToUse || !auth.currentUser) {
      setSaveErrorMessage('You must be logged in and have a saved story to save changes.');
      setShowSaveErrorModal(true);
      return;
    }

    setIsSavingSynopsis(true);
    try {
      const storyRef = doc(db, 'stories', storyIdToUse);
      await updateDoc(storyRef, {
        brief: editedSynopsis.trim() || null,
        updatedAt: new Date()
      });
      
      // Update local state
      setStory(prev => ({ ...prev, brief: editedSynopsis.trim() || null }));
      setIsEditingSynopsis(false);
      setSaveSuccessMessage('Synopsis updated successfully!');
      setShowSaveSuccessModal(true);
    } catch (error) {
      console.error('Error saving synopsis:', error);
      setSaveErrorMessage('Failed to save synopsis. Please try again.');
      setShowSaveErrorModal(true);
    } finally {
      setIsSavingSynopsis(false);
    }
  };

  const handleCancelEditSynopsis = () => {
    setEditedSynopsis(story?.brief || autoBrief || '');
    setIsEditingSynopsis(false);
  };

  // Handle about editing
  const handleEditAbout = () => {
    const currentAbout = story?.about || 'A creative storyteller who brings stories to life through beautiful artwork and engaging narratives.';
    setEditedAbout(currentAbout);
    setIsEditingAbout(true);
  };

  const handleSaveAbout = async () => {
    const storyIdToUse = id || story?.storyId;
    if (!storyIdToUse || !auth.currentUser) {
      setSaveErrorMessage('You must be logged in and have a saved story to save changes.');
      setShowSaveErrorModal(true);
      return;
    }

    setIsSavingAbout(true);
    try {
      const storyRef = doc(db, 'stories', storyIdToUse);
      await updateDoc(storyRef, {
        about: editedAbout.trim() || null,
        updatedAt: new Date()
      });
      
      // Update local state
      setStory(prev => ({ ...prev, about: editedAbout.trim() || null }));
      setIsEditingAbout(false);
      setSaveSuccessMessage('About section updated successfully!');
      setShowSaveSuccessModal(true);
    } catch (error) {
      console.error('Error saving about:', error);
      setSaveErrorMessage('Failed to save about section. Please try again.');
      setShowSaveErrorModal(true);
    } finally {
      setIsSavingAbout(false);
    }
  };

  const handleCancelEditAbout = () => {
    setEditedAbout(story?.about || 'A creative storyteller who brings stories to life through beautiful artwork and engaging narratives.');
    setIsEditingAbout(false);
  };

  // Debug: Log scene data to check for duplicates
  useEffect(() => {
    if (story && story.scenes && story.scenes.length > 0) {
      console.log('BookView scenes data:', story.scenes);
      story.scenes.forEach((scene, index) => {
        console.log(`Scene ${index + 1}:`, {
          title: scene.title,
          description: scene.description?.substring(0, 100) + '...',
          imageUrl: scene.imageUrl
        });
      });
    }
  }, [story]);

  // Navigation with page flip animation
  const goPrev = () => {
    if (page > 0 && !isFlipping) {
      setIsFlipping(true);
      setTimeout(() => {
        setPage(p => Math.max(0, p - 1));
        setIsFlipping(false);
      }, 300);
    }
  };

  const goNext = () => {
    if (page < totalPages - 1 && !isFlipping) {
      setIsFlipping(true);
      setTimeout(() => {
        setPage(p => Math.min(totalPages - 1, p + 1));
        setIsFlipping(false);
      }, 300);
    }
  };

  // Hide main content when artwork modal is open on mobile
  useEffect(() => {
    if (showArtworkModal && typeof window !== 'undefined' && window.innerWidth < 768) {
      document.body.style.overflow = 'hidden';
      // Hide main content
      const mainContent = document.querySelector('.book-view-container');
      if (mainContent) {
        mainContent.style.display = 'none';
      }
    } else {
      document.body.style.overflow = '';
      const mainContent = document.querySelector('.book-view-container');
      if (mainContent) {
        mainContent.style.display = '';
      }
    }
    return () => {
      document.body.style.overflow = '';
      const mainContent = document.querySelector('.book-view-container');
      if (mainContent) {
        mainContent.style.display = '';
      }
    };
  }, [showArtworkModal]);

  useEffect(() => {
    if (!loading) setHasLoadedOnce(true);
  }, [loading]);

  if (loading && !hasLoadedOnce) return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-amber-100 to-amber-200 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-amber-600 mx-auto mb-4"></div>
        <p className="text-amber-700 text-xl font-semibold">Opening your storybook...</p>
      </div>
    </div>
  );
  
  if (error) return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-amber-100 to-amber-200 flex items-center justify-center">
      <div className="bg-white/90 rounded-2xl p-8 shadow-xl border-2 border-amber-200">
        <p className="text-red-600 text-xl">{error}</p>
        <button 
          onClick={() => {
            // If return path exists, navigate back to it with state
            if (returnPath) {
              navigate(returnPath, { state: returnState });
            } else {
              // Fallback to browser back
              navigate(-1);
            }
          }}
          className="mt-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white px-6 py-2 rounded-xl hover:from-amber-600 hover:to-orange-600 transition-all duration-300"
        >
          Go Back
        </button>
      </div>
    </div>
  );
  
  if (!story) return null;

  const { title = '', author = '', coverUrl = '', scenes = [], originalArtwork = '', about: rawAbout = '', brief: rawBrief = '', storyTitle = '', storySetupData = {} } = story;
  
  // Remove AI references from existing story data
  const cleanAbout = rawAbout
    ?.replace(/Story created with AI-powered storyboarding technology/gi, 'A creative storyteller who brings stories to life through beautiful artwork and engaging narratives.')
    ?.replace(/A story created using advanced AI storyboarding technology/gi, 'A magical story that brings imagination to life through art and narrative.')
    ?.replace(/AI-powered storyboarding technology/gi, 'storytelling')
    ?.replace(/advanced AI storyboarding technology/gi, 'storytelling')
    ?.replace(/AI-powered|AI powered|advanced AI|artificial intelligence/gi, '')
    ?.replace(/storyboarding technology/gi, 'storytelling')
    ?.replace(/\s+/g, ' ')
    ?.trim() || '';
  
  const cleanBrief = rawBrief
    ?.replace(/A story created using advanced AI storyboarding technology/gi, 'A magical story that brings imagination to life through art and narrative.')
    ?.replace(/Story created with AI-powered storyboarding technology/gi, 'A magical story that brings imagination to life through art and narrative.')
    ?.replace(/AI-powered storyboarding technology/gi, 'storytelling')
    ?.replace(/advanced AI storyboarding technology/gi, 'storytelling')
    ?.replace(/AI-powered|AI powered|advanced AI|artificial intelligence/gi, '')
    ?.replace(/storyboarding technology/gi, 'storytelling')
    ?.replace(/\s+/g, ' ')
    ?.trim() || '';
  
  const about = cleanAbout || 'A creative storyteller who brings stories to life through beautiful artwork and engaging narratives.';
  const brief = cleanBrief || '';

  const totalPages = 4 + scenes.length;
  const currentSceneIndex = page >= 4 ? page - 4 : -1;
  const storyName = storyTitle || storySetupData?.storyTitle || title || 'Untitled Story';
  const headerTitle = currentSceneIndex >= 0
    ? `${storyName} - Scene ${currentSceneIndex + 1}`
    : storyName;

  // Book page rendering with sophisticated layout
  let content;
  if (page === 0) {
    // Cover - Sophisticated book cover design
    content = (
      <div className="flex flex-col items-center justify-center h-full relative">
        {/* Book cover with elegant design */}
        <div className="relative w-96 h-[500px] bg-gradient-to-br from-amber-100 to-orange-200 rounded-2xl shadow-2xl border-4 border-amber-300 overflow-hidden transform transition-all duration-700 hover:scale-105">
          {/* Cover image */}
          <div className="absolute inset-0">
            <img 
              src={coverUrl || originalArtwork} 
              alt="Book Cover" 
              className="w-full h-full object-cover"
            />
            {/* Gradient overlay for text readability */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
          </div>
          
          {/* Decorative elements */}
          <div className="absolute top-4 left-4 w-8 h-8 bg-amber-400 rounded-full opacity-80"></div>
          <div className="absolute top-8 right-6 w-4 h-4 bg-orange-300 rounded-full opacity-60"></div>
          <div className="absolute bottom-20 left-8 w-6 h-6 bg-amber-300 rounded-full opacity-70"></div>
          
          {/* Title and author */}
          <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
            <h1 className="text-4xl font-bold mb-2 drop-shadow-lg" style={{ fontFamily: "'Playfair Display', serif" }}>
              {storyName}
            </h1>
            <p className="text-xl opacity-90 drop-shadow-md">by {author || authorName || 'Anonymous'}</p>
            <div className="flex items-center mt-3 space-x-2">
              <div className="w-3 h-3 bg-amber-300 rounded-full"></div>
              <div className="w-3 h-3 bg-amber-300 rounded-full"></div>
              <div className="w-3 h-3 bg-amber-300 rounded-full"></div>
              <div className="w-3 h-3 bg-amber-300 rounded-full"></div>
            </div>
          </div>
        </div>
        
        {/* Decorative elements around the book */}
        <div className="absolute -top-4 -left-4 w-8 h-8 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full opacity-60 animate-pulse"></div>
        <div className="absolute -bottom-6 -right-6 w-12 h-12 bg-gradient-to-br from-orange-300 to-amber-400 rounded-full opacity-40 animate-pulse delay-1000"></div>
      </div>
    );
  } else if (page === 1) {
    // Book Brief - Elegant layout
    content = (
      <div className="flex flex-col items-center justify-center h-full px-4">
        <div className="max-w-4xl w-full">
          <div className="text-center mb-8">
            <h2 className="text-4xl font-bold text-amber-800 mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
              Story Synopsis
            </h2>
            <div className="w-32 h-1 bg-gradient-to-r from-amber-400 to-orange-500 mx-auto rounded-full"></div>
          </div>
          
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-12 shadow-xl border-2 border-amber-200 relative overflow-hidden">
            {/* Decorative corner elements */}
            <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-amber-200 to-transparent rounded-bl-full"></div>
            <div className="absolute bottom-0 left-0 w-16 h-16 bg-gradient-to-tr from-orange-200 to-transparent rounded-tr-full"></div>
            
            <div className="relative z-10">
              {/* Edit button - show if user is logged in */}
              {auth.currentUser && !isEditingSynopsis && (
                <div className="flex justify-end mb-4">
                  <button
                    onClick={handleEditSynopsis}
                    className="flex items-center gap-2 bg-gradient-to-r from-amber-500 to-amber-400 hover:from-amber-600 hover:to-amber-500 text-white px-4 py-2 rounded-lg shadow-md transition-all duration-300 text-sm font-semibold"
                  >
                    <FaEdit className="text-sm" />
                    Edit Synopsis
                  </button>
                </div>
              )}
              
              {isEditingSynopsis ? (
                <div className="space-y-4">
                  <textarea
                    value={editedSynopsis}
                    onChange={(e) => setEditedSynopsis(e.target.value)}
                    className="w-full p-4 border-2 border-amber-300 rounded-xl focus:border-amber-500 focus:outline-none text-amber-800 text-xl leading-relaxed resize-y min-h-[200px]"
                    style={{ fontFamily: "'Lora', serif" }}
                    placeholder="Enter your story synopsis..."
                  />
                  <div className="flex justify-end gap-3">
                    <button
                      onClick={handleCancelEditSynopsis}
                      disabled={isSavingSynopsis}
                      className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition-all duration-300 font-semibold disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveSynopsis}
                      disabled={isSavingSynopsis}
                      className="px-4 py-2 bg-gradient-to-r from-amber-500 to-amber-400 hover:from-amber-600 hover:to-amber-500 text-white rounded-lg transition-all duration-300 font-semibold disabled:opacity-50 flex items-center gap-2"
                    >
                      {isSavingSynopsis ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          Saving...
                        </>
                      ) : (
                        <>
                          <FaSave />
                          Save
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ) : (
                <p className="text-amber-800 text-xl leading-relaxed" style={{ fontFamily: "'Lora', serif" }}>
                  {brief || autoBrief || 'A magical story that brings imagination to life through art and narrative.'}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  } else if (page === 2) {
    // About the Author - Sophisticated layout
    content = (
      <div className="flex flex-col items-center justify-center h-full px-4">
        <div className="max-w-4xl w-full">
          <div className="text-center mb-8">
            <h2 className="text-4xl font-bold text-amber-800 mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
              About the Creator
            </h2>
            <div className="w-32 h-1 bg-gradient-to-r from-amber-400 to-orange-500 mx-auto rounded-full"></div>
          </div>
          
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-12 shadow-xl border-2 border-amber-200 relative overflow-hidden">
            {/* Decorative elements */}
            <div className="absolute top-4 left-4 w-8 h-8 bg-amber-300 rounded-full opacity-60"></div>
            <div className="absolute bottom-4 right-4 w-6 h-6 bg-orange-300 rounded-full opacity-60"></div>
            
            <div className="relative z-10">
              {/* Edit button - show if user is logged in */}
              {auth.currentUser && !isEditingAbout && (
                <div className="flex justify-end mb-4">
                  <button
                    onClick={handleEditAbout}
                    className="flex items-center gap-2 bg-gradient-to-r from-amber-500 to-amber-400 hover:from-amber-600 hover:to-amber-500 text-white px-4 py-2 rounded-lg shadow-md transition-all duration-300 text-sm font-semibold"
                  >
                    <FaEdit className="text-sm" />
                    Edit About
                  </button>
                </div>
              )}
              
              <div className="flex items-center mb-6">
                <div className="w-20 h-20 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center mr-6">
                  <FaBook className="text-white text-2xl" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-amber-800">{author || authorName || 'Anonymous'}</h3>
                  <p className="text-amber-600 text-lg">Story Creator</p>
                </div>
              </div>
              
              {isEditingAbout ? (
                <div className="space-y-4">
                  <textarea
                    value={editedAbout}
                    onChange={(e) => setEditedAbout(e.target.value)}
                    className="w-full p-4 border-2 border-amber-300 rounded-xl focus:border-amber-500 focus:outline-none text-amber-800 text-xl leading-relaxed resize-y min-h-[150px]"
                    style={{ fontFamily: "'Lora', serif" }}
                    placeholder="Tell us about the creator..."
                  />
                  <div className="flex justify-end gap-3">
                    <button
                      onClick={handleCancelEditAbout}
                      disabled={isSavingAbout}
                      className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition-all duration-300 font-semibold disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveAbout}
                      disabled={isSavingAbout}
                      className="px-4 py-2 bg-gradient-to-r from-amber-500 to-amber-400 hover:from-amber-600 hover:to-amber-500 text-white rounded-lg transition-all duration-300 font-semibold disabled:opacity-50 flex items-center gap-2"
                    >
                      {isSavingAbout ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          Saving...
                        </>
                      ) : (
                        <>
                          <FaSave />
                          Save
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ) : (
                <p className="text-amber-800 text-xl leading-relaxed" style={{ fontFamily: "'Lora', serif" }}>
                  {about || 'A creative storyteller who brings stories to life through beautiful artwork and engaging narratives.'}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  } else if (page === 3) {
    // Index - Elegant table of contents
    content = (
      <div className="flex flex-col items-center justify-center h-full px-4">
        <div className="max-w-4xl w-full">
          <div className="text-center mb-8">
            <h2 className="text-4xl font-bold text-amber-800 mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
              Table of Contents
            </h2>
            <div className="w-32 h-1 bg-gradient-to-r from-amber-400 to-orange-500 mx-auto rounded-full"></div>
          </div>
          
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-12 shadow-xl border-2 border-amber-200">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {scenes.map((scene, idx) => (
                <div key={idx} className="flex items-center justify-between p-6 bg-white/70 rounded-xl border border-amber-200 hover:bg-white/90 transition-all duration-300 cursor-pointer group hover:shadow-lg">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center text-white font-bold text-lg mr-4">
                      {idx + 1}
                    </div>
                    <span className="text-amber-800 font-semibold text-xl group-hover:text-amber-900 transition-colors">
                      {scene.title || `Scene ${idx + 1}`}
                    </span>
                  </div>
                  <FaEye className="text-amber-500 opacity-0 group-hover:opacity-100 transition-opacity text-xl" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  } else {
    // Scene pages - Sophisticated two-page spread layout with dynamic sizing
    const idx = page - 4;
    const currentScene = scenes[idx];
    
    // Raw description for editing
    const rawDescription = currentScene?.description || '';
    
    // Calculate text length to determine layout
    const textLength = rawDescription.length;
    const isLongText = textLength > 500;
    const isMediumText = textLength > 200 && textLength <= 500;
    const isShortText = textLength <= 200;
    
    // Size the scene box to match artwork image
    const artworkHeight = isLongText ? 'h-[300px] md:h-[400px]' : isMediumText ? 'h-[350px] md:h-[450px]' : 'h-[400px] md:h-[500px]';
    const artworkWidth = isLongText ? 'max-w-md' : isMediumText ? 'max-w-lg' : 'max-w-xl';
    const textContainerHeight = isLongText ? 'h-[300px] md:h-[400px]' : isMediumText ? 'h-[350px] md:h-[450px]' : 'h-[400px] md:h-[500px]';
    
    content = (
      <div className="flex flex-col md:flex-row w-full gap-4 md:gap-6 px-2 md:px-4 items-start">
        {/* Left page - Artwork (dynamic size based on text) */}
        <div className={`w-full md:w-1/2 flex items-start justify-center`}>
          <div className={`relative w-full ${artworkWidth}`}>
            <div className="bg-gradient-to-br from-amber-100 to-orange-100 rounded-xl md:rounded-2xl p-2 md:p-4 shadow-xl border-2 border-amber-200">
              <img
                src={currentScene?.imageUrl || coverUrl || originalArtwork}
            alt={`Scene ${idx + 1} Artwork`}
                className={`w-full ${artworkHeight} object-contain md:object-cover rounded-lg md:rounded-xl shadow-lg border-2 border-amber-300`}
              />
              {/* Scene number badge - moved to top-left */}
              <div className="absolute -top-2 md:-top-3 -left-2 md:-left-3 w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center text-white font-bold text-base md:text-xl shadow-lg">
                {idx + 1}
              </div>
              {/* Expand icon */}
              <button
                className="absolute top-1 md:top-2 right-1 md:right-2 bg-white/80 hover:bg-white text-amber-800 border border-amber-200 rounded-full p-1.5 md:p-2 shadow"
                title="Expand artwork"
                onClick={() => setShowArtworkModal(true)}
              >
                <FaExpand className="w-3 h-3 md:w-4 md:h-4" />
              </button>
            </div>
          </div>
        </div>
        
        {/* Right page - Scene description (always fits) */}
        <div className={`w-full md:w-1/2 flex flex-col justify-start`}> 
          <div className="w-full">
            {/* Scene heading removed to align the text box with artwork */}
            
            {/* Edit button above text box removed (duplicate) */}

            <div className={`bg-gradient-to-br from-amber-50 to-orange-50 rounded-lg md:rounded-xl p-3 md:p-6 shadow-lg border-2 border-amber-200 relative overflow-hidden min-h-[200px] ${textContainerHeight} ${artworkWidth}`}>
              {/* Decorative corner */}
              <div className="absolute top-0 right-0 w-12 h-12 md:w-16 md:h-16 bg-gradient-to-bl from-amber-200 to-transparent rounded-bl-full"></div>
              
              <div className="relative z-10 h-full overflow-y-auto pr-2">
                {/* Scene content: switches between read-only and edit */}
                {!isEditing ? (
                  <div className={`text-amber-800 ${isLongText ? 'text-sm md:text-base' : isMediumText ? 'text-base md:text-lg' : 'text-base md:text-xl'}`} style={{ fontFamily: "'Lora', serif" }}>
                    {rawDescription || <span className="text-amber-400">Write or edit your scene here...</span>}
                  </div>
                ) : (
                  <textarea
                    className={`w-full h-full bg-transparent text-amber-800 outline-none resize-none ${isLongText ? 'text-sm md:text-base' : isMediumText ? 'text-base md:text-lg' : 'text-base md:text-xl'}`}
                    style={{ fontFamily: "'Lora', serif" }}
                    value={draftDescriptions[idx] ?? rawDescription}
                    ref={descRef}
                    data-testid="scene-description-editor"
                    spellCheck={false}
                    autoCorrect="off"
                    autoCapitalize="sentences"
                    placeholder="Write or edit your scene here..."
                    onChange={(e) => {
                      const val = e.target.value;
                      setDraftDescriptions(prev => ({ ...prev, [idx]: val }));
                    }}
                    onBlur={async (e) => {
                      try {
                        const newDesc = e.target.value;
                        const sceneIdx = page - 4;
                        const updatedScenes = [...scenes];
                        updatedScenes[sceneIdx] = { ...updatedScenes[sceneIdx], description: newDesc };
                        setStory((prev) => ({ ...prev, scenes: updatedScenes }));
                        setDraftDescriptions(prev => ({ ...prev, [idx]: newDesc }));
                        await fetch(`${backendBase}/api/stories/${id}`, {
                          method: 'PUT',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ ...story, scenes: updatedScenes })
                        });
                      } catch (err) {
                        console.error('Failed to update scene description:', err);
                      } finally {
                        setIsEditing(false);
                      }
                    }}
                  />
                )}
                {/* (Edit button moved above box) */}
              </div>
            </div>
            
            {/* Scene metadata removed: genre pill no longer shown */}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {loading && hasLoadedOnce && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-30 bg-white/90 border border-amber-200 text-amber-800 text-xs px-3 py-1.5 rounded-full shadow">
          Refreshing story...
        </div>
      )}
      {/* Artwork modal - Fullscreen on mobile, focus only on artwork */}
      {showArtworkModal && typeof document !== 'undefined' && createPortal(
        <div 
          className="fixed inset-0 flex items-center justify-center" 
          style={{ 
            zIndex: 999999,
            backgroundColor: typeof window !== 'undefined' && window.innerWidth < 768 ? 'rgba(0, 0, 0, 1)' : 'rgba(0, 0, 0, 0.8)'
          }}
          onClick={() => setShowArtworkModal(false)}
        >
          <div 
            className="relative w-full h-full flex items-center justify-center p-2 md:p-4" 
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={currentSceneIndex >= 0 ? (scenes[currentSceneIndex]?.imageUrl || coverUrl || originalArtwork) : (coverUrl || originalArtwork)}
              alt="Expanded artwork"
              className="max-w-full max-h-full w-auto h-auto object-contain rounded-lg md:rounded-2xl shadow-2xl border-2 md:border-4 border-amber-200"
              style={{
                maxHeight: typeof window !== 'undefined' && window.innerWidth < 768 ? '95vh' : '90vh'
              }}
            />
            <button
              className="absolute bg-white/90 hover:bg-white text-amber-800 border border-amber-200 rounded-full p-2 md:px-3 md:py-1 shadow-lg z-10"
              onClick={() => setShowArtworkModal(false)}
              style={{
                minWidth: '40px',
                minHeight: '40px',
                top: typeof window !== 'undefined' && window.innerWidth < 768 
                  ? `max(0.5rem, calc(env(safe-area-inset-top, 0px) + 0.5rem))` 
                  : '1rem',
                right: typeof window !== 'undefined' && window.innerWidth < 768
                  ? `max(0.5rem, calc(env(safe-area-inset-right, 0px) + 0.5rem))`
                  : '1rem'
              }}
            >
              <FaTimes className="w-4 h-4 md:w-5 md:h-5" />
            </button>
          </div>
        </div>,
        document.body
      )}
      {/* Artmosphere-styled modal for delete confirmation */}
      <CustomModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Scene?"
        message={deleteIndex >= 0 ? `Are you sure you want to delete Scene ${deleteIndex + 1}? This action cannot be undone.` : ''}
        type="warning"
        onConfirm={async () => {
          if (deleteIndex < 0) return;
          try {
            const newScenes = scenes.filter((_, i) => i !== deleteIndex).map((s, i) => ({ ...s, sceneNumber: i + 1 }));
            setStory(prev => ({ ...prev, scenes: newScenes }));
            setPage(p => Math.min(p, 3 + newScenes.length));
            await fetch(`${backendBase}/api/stories/${id}`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ ...story, scenes: newScenes })
            });
          } catch (err) {
            console.error('Failed to delete scene:', err);
          } finally {
            setShowDeleteModal(false);
            setDeleteIndex(-1);
          }
        }}
      />
      <style>{`
        @keyframes pageFlip {
          0% { transform: rotateY(0deg); }
          50% { transform: rotateY(90deg); }
          100% { transform: rotateY(0deg); }
        }
        
        .page-flip {
          animation: pageFlip 0.6s ease-in-out;
          transform-style: preserve-3d;
        }
        
        .book-container {
          perspective: 1000px;
        }
      `}</style>
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-amber-100 to-amber-200 p-1 md:p-2 font-serif book-view-container" style={{ 
        paddingBottom: 'max(2rem, calc(env(safe-area-inset-bottom, 0px) + 2rem))',
        paddingTop: 'max(0.5rem, calc(env(safe-area-inset-top, 0px) + 0.5rem))'
      }}>
        <div className={`min-h-screen md:h-screen max-w-7xl mx-auto rounded-xl md:rounded-3xl shadow-2xl bg-white/90 border-2 md:border-4 border-amber-100 overflow-hidden relative transition-all duration-1000 book-container flex flex-col ${isBookOpen ? 'transform scale-100 opacity-100' : 'transform scale-95 opacity-0'}`}>
        {/* Navigation Bar */}
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between px-2 md:px-6 py-2 md:py-4 bg-gradient-to-r from-amber-100 to-orange-100 border-b-2 border-amber-200 gap-2 md:gap-0">
            <button 
            onClick={() => {
              // If return path exists, navigate back to it with state
              if (returnPath) {
                navigate(returnPath, { state: returnState });
              } else {
                // Fallback to default navigation
                navigate('/my-art-stories');
              }
            }} 
              className="flex items-center gap-1 md:gap-2 text-amber-700 hover:text-amber-900 font-semibold transition-colors duration-300 text-xs md:text-base"
            >
              <FaArrowLeft className="w-3 h-3 md:w-4 md:h-4" /> <span className="hidden sm:inline">Back to Studio</span><span className="sm:hidden">Back</span>
          </button>
          <div className="flex items-center gap-2 md:gap-4">
              <FaBook className="text-amber-600 text-base md:text-xl" />
              <span className="text-sm md:text-xl font-bold text-amber-800 truncate max-w-[150px] md:max-w-none">{headerTitle}</span>
            </div>
            <div className="flex items-center gap-1 md:gap-2 flex-wrap">
              <div className="w-2 h-2 md:w-4 md:h-4 bg-amber-500 rounded-full"></div>
              <span className="text-amber-700 font-semibold text-xs md:text-base">{scenes.length} Scenes</span>
              {/* Inline Delete and Edit controls for scene */}
              {page >= 4 && page - 4 < scenes.length && (
                <button
                  onClick={() => {
                    setIsEditing(true);
                    setTimeout(() => {
                      if (descRef.current) {
                        descRef.current.focus();
                        const len = descRef.current.value.length;
                        descRef.current.setSelectionRange(len, len);
                      }
                    }, 50);
                  }}
                  className="ml-1 md:ml-2 flex items-center gap-1 md:gap-2 bg-white/70 backdrop-blur-sm border border-amber-200 text-amber-800 px-2 py-1 md:px-3 md:py-2 rounded-full shadow hover:bg-white text-xs md:text-base"
                  title="Edit this scene"
                >
                  <FaEdit className="w-3 h-3 md:w-4 md:h-4" />
                  <span className="hidden sm:inline">Edit</span>
                </button>
              )}
              {page >= 4 && page - 4 < scenes.length && (
                <button
                  onClick={() => {
                    setDeleteIndex(page - 4);
                    setShowDeleteModal(true);
                  }}
                  className="ml-1 md:ml-2 flex items-center gap-1 md:gap-2 bg-gradient-to-r from-red-500 to-rose-400 text-white px-2 py-1 md:px-3 md:py-2 rounded-full shadow hover:from-red-600 hover:to-rose-500 transition-all duration-300 text-xs md:text-base"
                  title="Delete this scene"
                >
                  <FaTrash className="w-3 h-3 md:w-4 md:h-4" />
                  <span className="hidden sm:inline">Delete</span>
                </button>
              )}
              {/* Add Scene button on last scene page */}
              {page === 3 + scenes.length && (
                <button
                  onClick={() => navigate(`/storyboarding-studio?storyId=${id || story.id || story.storyId || ''}&editScene=${(scenes?.length || 0) + 1}`)}
                  className="ml-1 md:ml-3 flex items-center gap-1 md:gap-2 bg-gradient-to-r from-amber-500 to-orange-400 text-white px-2 py-1 md:px-4 md:py-2 rounded-full shadow hover:from-amber-600 hover:to-orange-500 transition-all duration-300 text-xs md:text-base"
                >
                  + Scene
                </button>
              )}
            </div>
          </div>
          
          {/* Book Content with Page Flip Animation */}
          <div className={`flex-1 flex flex-col items-center justify-center p-2 md:p-4 transition-all duration-300 ${isFlipping ? 'page-flip' : ''} overflow-y-auto`} style={{ 
            minHeight: 0, // Allow flex to control height
            maxHeight: 'calc(100vh - 200px - env(safe-area-inset-bottom, 0px) - env(safe-area-inset-top, 0px))'
          }}>
            <div className={`w-full h-full transition-all duration-300 ${isFlipping ? 'opacity-0' : 'opacity-100'}`}>
          {content}
        </div>
          </div>
          
        {/* Page Navigation - Fixed at bottom */}
          <div className="flex flex-row items-center justify-between px-2 md:px-8 py-3 md:py-4 bg-gradient-to-r from-amber-50 to-orange-50 border-t-2 border-amber-100 gap-1 md:gap-0 flex-shrink-0" style={{ 
            paddingBottom: 'max(1.5rem, calc(env(safe-area-inset-bottom, 0px) + 1.5rem))',
            paddingTop: 'max(0.75rem, calc(env(safe-area-inset-top, 0px) + 0.75rem))',
            position: 'relative',
            zIndex: 10
          }}>
          <button
            onClick={goPrev}
              disabled={page === 0 || isFlipping}
              className={`flex items-center gap-1 px-2 py-1.5 md:px-6 md:py-3 rounded-lg md:rounded-xl font-semibold transition-all duration-300 text-xs md:text-base flex-shrink-0 z-10 ${
                page === 0 || isFlipping
                  ? 'bg-amber-100 text-amber-300 cursor-not-allowed' 
                  : 'bg-gradient-to-r from-amber-400 to-orange-400 text-white hover:from-amber-500 hover:to-orange-500 shadow-lg hover:shadow-xl transform hover:scale-105'
              }`}
            >
              <FaArrowLeft className="w-3 h-3 md:w-4 md:h-4" /> <span className="hidden sm:inline">Previous</span><span className="sm:hidden">Prev</span>
          </button>
            
            <div className="flex flex-row items-center gap-1 md:gap-4 flex-1 justify-center min-w-0 px-1">
              <div className="bg-white/80 rounded-full px-2 py-1 md:px-6 md:py-3 shadow-md flex-shrink-0">
                <span className="text-amber-700 font-bold text-xs md:text-lg whitespace-nowrap">Page {page + 1} of {totalPages}</span>
              </div>
              <div className="hidden md:flex space-x-1 md:space-x-2">
                {Array.from({ length: totalPages }, (_, i) => (
                  <div
                    key={i}
                    className={`w-2 h-2 md:w-3 md:h-3 rounded-full transition-all duration-300 ${
                      i === page ? 'bg-amber-500' : 'bg-amber-200'
                    }`}
                  />
                ))}
              </div>
            </div>
            
          <button
            onClick={goNext}
              disabled={page === totalPages - 1 || isFlipping}
              className={`flex items-center gap-1 px-2 py-1.5 md:px-6 md:py-3 rounded-lg md:rounded-xl font-semibold transition-all duration-300 text-xs md:text-base flex-shrink-0 z-10 ${
                page === totalPages - 1 || isFlipping
                  ? 'bg-amber-100 text-amber-300 cursor-not-allowed' 
                  : 'bg-gradient-to-r from-amber-400 to-orange-400 text-white hover:from-amber-500 hover:to-orange-500 shadow-lg hover:shadow-xl transform hover:scale-105'
              }`}
          >
            <span className="hidden sm:inline">Next</span><span className="sm:hidden">Next</span> <FaArrowRight className="w-3 h-3 md:w-4 md:h-4" />
          </button>
        </div>
      </div>
    </div>
    
    {/* Save Success Modal - Artmosphere Aesthetics */}
    <CustomModal
      isOpen={showSaveSuccessModal}
      onClose={() => setShowSaveSuccessModal(false)}
      title="Success!"
      message={saveSuccessMessage}
      type="success"
      confirmText="OK"
    />
    
    {/* Save Error Modal - Artmosphere Aesthetics */}
    <CustomModal
      isOpen={showSaveErrorModal}
      onClose={() => setShowSaveErrorModal(false)}
      title="Error"
      message={saveErrorMessage}
      type="error"
      confirmText="OK"
    />
    </>
  );
} 